import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { swalMessage } from '@components/common';
import { Button } from '@components/common/Button';
import Disclaimer from '@components/common/Disclaimer';
import { TabButton } from '@components/common/TabNavigation';
import { ToggleBtn } from '@components/common/ToggleSwitch';
import { BackgroundLoading } from '@components/Loading';
import { ModalBackground } from '@components/modal/choiceModal';
import LogTab from '@components/modal/detailsModal/LogTab';
import MileageTab from '@components/modal/detailsModal/MileageTab';
import PayTab from '@components/modal/detailsModal/PayTab';
import TicketTab from '@components/modal/detailsModal/TicketTab';
import {
  createMemo,
  deleteMemo,
  getMemo,
  userInfoUpdate,
  userList,
  userTicketAccess,
} from '@lib/api';
import {
  deleteUserFace,
  getUserFace,
  patchResetUserPin,
  postSendUserBarcode,
} from '@lib/api/v2/user';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import { sendQrCode } from '@lib/socket';
import useAppStore from '@store/useAppStore';
import { media } from '@styles/mediaQuery';
import { firstArray } from '@util/array';
import { swalReturnWarning } from '@util/commonFunctions';
import Format from '@util/formatter';
import styled from 'styled-components';
import Swal from 'sweetalert2';

// 타입 정의
interface DetailsModalProps {
  isOpen: boolean;
  close: () => void;
  seat: any;
  back?: boolean;
  setData?: (merchantId: number) => void;
  zIndex?: number;
}

interface MemoItem {
  id: number;
  memo: string;
  createdAt: string;
}

interface MerchantUserInfo {
  id: number;
  name: string;
  phoneNum: string;
  gender: number | null;
  createdAt: string;
  parentPhoneNum: string;
  smsDeny: boolean;
  easyTicketAccessUser: boolean;
  superEnter: number;
  birth: string | null;
}

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  padding: 0 20px;
  margin: 10px 0;
  width: 100%;
  overflow-y: auto;
  border-bottom: solid 1px #ccc;
  > div > button {
    width: 100px;
    margin-right: 0;
  }
  ${media.mobile`
    > div {
      width: 100%;
      display: flex;
    }
    flex-direction: column-reverse;
    padding: 0 10px;
    > div > button {
      font-size: 13px;
      margin-right: 1px;
    }
  `}
`;

const TabList1 = styled(ToggleWrapper)`
  justify-content: flex-start;
  gap: 0.5rem;
  margin: 0;

  ${media.mobile`
    > div {
      flex-wrap: wrap;
    }
  `}
`;

// UserInfoTable 스타일 (클래스업 스타일)
const UserInfoTableContainer = styled.div`
  width: 100%;
  padding: 20px;

  .section-title {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 0.5rem;
    text-align: left;
  }
`;

const StyledUserInfoTable = styled.table`
  margin: 0.5rem 0 1.5rem;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
  width: 100%;

  tr,
  td {
    text-align: start;
    padding: 0.5rem;
    color: #555;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 100%;
  }

  .key {
    background: #f5f5f5;
    width: 15%;
    text-align: left;
    min-width: 6.25rem;
    font-weight: 600;
    word-break: keep-all;
    overflow-wrap: break-word;
  }

  .value {
    width: 100%;
    word-break: break-word;
    overflow-wrap: break-word;
  }
`;

// 메모 관련 styled components (클래스업 스타일)
const MemoCreateContainer = styled.div`
  border: 1px solid #e9e9e9;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  button {
    width: auto;
    margin: 0;
    align-self: flex-end;
    padding: 8px 24px;
  }
`;

const MemoTextareaWrapper = styled.div`
  position: relative;
  width: 100%;

  textarea {
    width: 100%;
    min-height: 120px;
    padding: 12px;
    border: 1px solid #e1e1e1;
    border-radius: 4px;
    font-size: 14px;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #04c9db;
    }
  }
`;

const MemoTextLength = styled.div`
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  font-size: 12px;
  color: #999;

  span {
    color: #04c9db;
    font-weight: 500;
  }
`;

const MemoListContainer = styled.div`
  padding: 1.5rem;
  border-radius: 4px;
  background: #f9f9f9;
  border: 1px solid #e9e9e9;
  margin-bottom: 1.5rem;

  h4 {
    font-weight: 500;
    font-size: 16px;
    text-align: start;
    margin-bottom: 15px;
  }
`;

const MemoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 400px;
  overflow-y: auto;
`;

const MemoListItem = styled.li`
  background: #ffffff;
  border-radius: 4px;
  padding: 18px;
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MemoContent = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MemoFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MemoTimestamp = styled.span`
  color: #999;
  font-size: 12px;
  font-weight: 500;
`;

const MemoDeleteBtn = styled.button`
  padding: 4px 12px;
  background: #fbebeb;
  color: #d43434;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    background: #f5d5d5;
  }
`;

const NoMemoMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
`;

const buildModalStyle = (z: number) => `
  .details-modal-backdrop {
    z-index: ${z - 50} !important;
  }
  .details-modal {
    z-index: ${z} !important;
  }
`;

const DetailsModal = ({ isOpen, close, seat, back, setData, zIndex = 450 }: DetailsModalProps) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();
  const { merchantData } = useAppStore();
  // 채팅처럼 서버가 지점 범위를 확정해 전달한 화면에서는 전역 점주 정보 로드를
  // 기다리지 않고 해당 회원의 merchantId로 상세를 즉시 조회한다.
  const merchantId = seat?.merchantId || merchantData?.id;

  const [tabMenuBar, setTabMenuBar] = useState(5);
  const [accessCheck, setAccessCheck] = useState(seat?.easyTicketAccessUser);
  const [smsDenyCheck, setSmsDenyCheck] = useState(seat?.smsDeny);
  const [superEnterCheck, setSuperEnterCheck] = useState(seat?.superEnter);
  const [memo, setMemo] = useState('');
  const [memolist, setMemoList] = useState<MemoItem[]>([]);
  const [memoTrigger, setMemoTrigger] = useState(false);
  const [parentPhoneNum, setParentPhoneNum] = useState(seat?.parentPhoneNum);
  const [merchantUserInfo, setMerchantUserInfo] = useState<MerchantUserInfo | null>(null);

  // 회원 기본 정보 수정 상태
  const [editName, setEditName] = useState(seat?.name || '');
  const [editGender, setEditGender] = useState<number | null>(null);

  // 보호자 기능 고도화 (LETMEUP-319): 회원유형/보호자명/보호자 알림 수신동의
  const [editUserType, setEditUserType] = useState<number>(seat?.userType ?? 0);
  const [editParentName, setEditParentName] = useState(seat?.parentName || '');
  const [parentAlertConsentCheck, setParentAlertConsentCheck] = useState<number>(
    seat?.parentAlertConsent ?? 1,
  );

  // 생년월일 (LETMEUP-788 나이제한): 점주가 회원 상세에서 직접 입력/수정.
  // 키오스크 셀프 백필/백오피스 수정과 같은 tb_user.birth 컬럼을 공유한다.
  const [editBirth, setEditBirth] = useState(
    seat?.birth ? dayjs(seat.birth).format('YYYY-MM-DD') : '',
  );

  // 초기값 저장 (변경 감지용)
  const [initialUserInfo, setInitialUserInfo] = useState({
    name: seat?.name || '',
    gender: null as number | null,
    parentPhoneNum: seat?.parentPhoneNum || '',
    userType: (seat?.userType ?? 0) as number,
    parentName: seat?.parentName || '',
    birth: seat?.birth ? dayjs(seat.birth).format('YYYY-MM-DD') : '',
  });

  //로딩componernt
  const [isLoading, setIsLoading] = useState(true);

  //userId
  const [userId, setUserId] = useState(seat?.userId ? seat?.userId : seat?.id);

  const getUserData = async (merchantId: number, phoneNum: string) => {
    try {
      const resultData = await createRequest(userList, merchantId, phoneNum);
      const currentUser = firstArray<any>(resultData?.data)[0];
      if (!currentUser) return;
      console.log(currentUser);
      setMerchantUserInfo(currentUser);
      setSmsDenyCheck(currentUser.smsDeny);
      setAccessCheck(currentUser.easyTicketAccessUser);
      setSuperEnterCheck(currentUser.superEnter);
      setUserId(currentUser.id);
      setParentPhoneNum(currentUser.parentPhoneNum);

      // 회원 기본 정보 수정 상태 초기화
      setEditName(currentUser.name || '');
      setEditGender(currentUser.gender);
      setEditUserType(currentUser.userType ?? 0);
      setEditParentName(currentUser.parentName || '');
      setParentAlertConsentCheck(currentUser.parentAlertConsent ?? 1);
      setEditBirth(currentUser.birth ? dayjs(currentUser.birth).format('YYYY-MM-DD') : '');

      // 초기값 저장
      setInitialUserInfo({
        name: currentUser.name || '',
        gender: currentUser.gender,
        parentPhoneNum: currentUser.parentPhoneNum || '',
        userType: currentUser.userType ?? 0,
        parentName: currentUser.parentName || '',
        birth: currentUser.birth ? dayjs(currentUser.birth).format('YYYY-MM-DD') : '',
      });
    } catch (error) {
      catchError(
        error,
        '회원 설정기능 관련 데이터를 가져오는 도중 에러가 발생했습니다.\n다시 시도해주세요.',
      );
    }
  };

  const initDetailsModal = async (mid: number) => {
    try {
      if (!seat?.phoneNum) {
        setIsLoading(false);
        return;
      }
      // 회원 기본 정보만 로드 (탭별 데이터는 탭 클릭 시 로드)
      await getUserData(mid, seat?.phoneNum);
      setIsLoading(false);
    } catch (error) {
      catchError(error);
      setIsLoading(false);
    }
  };

  const accessChange = async () => {
    try {
      if (!seat?.phoneNum) return close();
      await createRequest(userTicketAccess, {
        smsDeny: smsDenyCheck,
        access: accessCheck,
        userId: userId,
        superEnter: superEnterCheck,
        merchantId: seat.merchantId,
      });
      close();
      if (setData) {
        setData(seat.merchantId);
      }
    } catch (error) {
      catchError(error);
    }
  };

  const saveUserSettings = async (
    updatedValues: Partial<{ smsDeny: boolean; access: boolean; superEnter: number }> = {},
  ) => {
    try {
      if (!seat?.phoneNum) return;
      await createRequest(userTicketAccess, {
        smsDeny: updatedValues.smsDeny !== undefined ? updatedValues.smsDeny : smsDenyCheck,
        access: updatedValues.access !== undefined ? updatedValues.access : accessCheck,
        userId: userId,
        superEnter:
          updatedValues.superEnter !== undefined ? updatedValues.superEnter : superEnterCheck,
        merchantId: seat.merchantId,
      });
      if (setData) {
        setData(seat.merchantId);
      }
    } catch (error) {
      catchError(error);
      Swal.fire({
        icon: 'error',
        title: '저장에 실패했습니다.',
        text: '다시 시도해주세요.',
      });
    }
  };

  // 메모 탭 데이터 fetch
  useEffect(() => {
    if (!merchantId || tabMenuBar !== 3) return;
    if (seat?.phoneNum) {
      getMemoList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabMenuBar, merchantId]);

  const memoHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMemo(e.target.value);
    if (e.target.value) {
      setMemoTrigger(true);
    } else {
      setMemoTrigger(false);
    }
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      merchantId: seat.merchantId,
      phoneNum: seat.phoneNum,
      memo: memo,
    };

    if (memo && memoTrigger) {
      try {
        const res = await createRequest(createMemo, data);
        if (res.status === 200) {
          getMemoList();
          setMemo('');
          setMemoTrigger(false);
        } else if (res.status === 202) {
          Swal.fire({
            icon: 'error',
            title: '메모 작성에 실패했습니다.',
            timer: 2000,
          });
        }
      } catch (err) {
        catchError(err);
      }
    } else {
      Swal.fire({
        icon: 'error',
        title: '내용을 입력해주세요.',
      });
    }
  };

  const getMemoList = async () => {
    const merchantId = seat.merchantId;
    const phoneNum = seat.phoneNum;
    setIsLoading(true);
    try {
      const res = await createRequest(getMemo, merchantId, phoneNum);

      if (res.status === 200) {
        setMemoList(res.data);
      }
      setIsLoading(false);
    } catch (err) {
      catchError(err);
    }
  };

  const deleteHandler = async (id: number) => {
    const merchantId = seat.merchantId;
    try {
      const res = await createRequest(deleteMemo, merchantId, id);
      if (res.status === 200) {
        getMemoList();
      }
    } catch (err) {
      catchError(err);
    }
  };

  const saveBasicUserInfo = async () => {
    try {
      // 변경된 필드만 포함
      const updatedFields: {
        merchantId?: number;
        userId?: number;
        userName?: string;
        gender?: number | null;
        parentPhoneNum?: string;
        userType?: number;
        parentName?: string;
        birth?: string;
      } = {
        merchantId: seat?.merchantId,
        userId: userId,
      };

      if (editName !== initialUserInfo.name) {
        updatedFields.userName = editName.trim();
      }

      if (editGender !== initialUserInfo.gender) {
        updatedFields.gender = editGender;
      }

      if (parentPhoneNum !== initialUserInfo.parentPhoneNum) {
        updatedFields.parentPhoneNum = parentPhoneNum;
      }

      if (editUserType !== initialUserInfo.userType) {
        updatedFields.userType = editUserType;
      }

      if (editParentName !== initialUserInfo.parentName) {
        updatedFields.parentName = editParentName.trim();
      }

      // 생년월일 (LETMEUP-788 나이제한): 형식 검증 후 변경 시에만 전송. 서버가 YYYY-MM-DD로 정규화.
      if (editBirth !== initialUserInfo.birth) {
        const birthInput = editBirth.trim();
        if (
          birthInput &&
          !/^\d{6}$/.test(birthInput) &&
          !/^\d{8}$/.test(birthInput) &&
          !/^\d{4}-\d{2}-\d{2}$/.test(birthInput)
        ) {
          swalMessage('error', '생년월일을 6자리(YYMMDD) 또는 YYYY-MM-DD 형식으로 입력해주세요.');
          return;
        }
        updatedFields.birth = birthInput;
      }

      // 변경된 내용이 없으면 저장하지 않음
      if (Object.keys(updatedFields).length === 2) {
        swalMessage('info', '변경된 내용이 없습니다.');
        return;
      }

      await createRequest(userInfoUpdate, updatedFields);

      swalMessage('success', '회원 정보가 변경되었습니다.');

      // 데이터 새로고침
      if (setData) {
        setData(seat.merchantId);
      }
      getUserData(seat.merchantId, seat.phoneNum);
    } catch (error) {
      catchError(error);
      swalMessage('error', '변경에 실패했습니다.\n다시 시도해주세요.');
    }
  };

  const handleIssueBarcode = (merchantId: number, phoneNum: string, qrCode?: string) => {
    if (!qrCode) {
      return swalReturnWarning(
        '바코드를 발급받지 않은 회원입니다.\n회원이 바코드를 발급받은 경우에만 출력이 가능합니다.',
      );
    }
    sendQrCode(merchantId, phoneNum, qrCode);
  };

  // 회원 얼굴 등록 정보 (조회/삭제)
  const [faceInfo, setFaceInfo] = useState<{ hasFace: boolean; faceImg: string | null }>({
    hasFace: false,
    faceImg: null,
  });

  // 회원 얼굴 등록 정보 조회 (모달 진입 시 자동 + 삭제 후 갱신)
  const fetchUserFace = async (phoneNum?: string) => {
    if (!phoneNum) return;
    try {
      const res = await getUserFace(phoneNum);
      setFaceInfo({
        hasFace: !!res.data?.hasFace,
        faceImg: res.data?.faceImg ?? null,
      });
    } catch (error) {
      setFaceInfo({ hasFace: false, faceImg: null });
    }
  };

  useEffect(() => {
    if (isOpen && seat?.phoneNum) {
      fetchUserFace(seat.phoneNum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seat?.phoneNum]);

  // 회원 얼굴 삭제 (점주) — Rekognition + S3 + DB 논리삭제
  const handleDeleteFace = async (phoneNum: string) => {
    if (!phoneNum) {
      return swalReturnWarning('회원 휴대폰번호를 확인할 수 없습니다.');
    }
    const confirm = await Swal.fire({
      title: '얼굴 정보 삭제',
      text: '등록된 회원 얼굴 정보를 삭제합니다.\n삭제 후에는 얼굴 인식 입장이 불가능합니다.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    });
    if (!confirm.isConfirmed) return;
    try {
      await createRequest(deleteUserFace, phoneNum);
      swalMessage('success', '얼굴 정보가 삭제되었습니다.');
      fetchUserFace(phoneNum);
    } catch (error) {
      catchError(error);
      swalMessage('error', '얼굴 삭제에 실패했습니다.\n다시 시도해주세요.');
    }
  };

  // 회원에게 바코드 링크를 문자로 발송 (바코드 재발급 → 문자 전송)
  const handleSendBarcode = async (phoneNum: string) => {
    if (!phoneNum) {
      return swalReturnWarning('회원 휴대폰번호를 확인할 수 없습니다.');
    }
    const confirm = await Swal.fire({
      title: '바코드 문자 발송',
      text: '회원에게 바코드 링크를 문자로 전송합니다.\n새 바코드가 발급되어 기존 바코드는 사용할 수 없게 됩니다.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '발송',
      cancelButtonText: '취소',
    });
    if (!confirm.isConfirmed) return;
    try {
      await createRequest(postSendUserBarcode, phoneNum);
      swalMessage('success', '바코드 링크를 문자로 전송했습니다.');
    } catch (error) {
      catchError(error);
      swalMessage('error', '문자 전송에 실패했습니다.\n다시 시도해주세요.');
    }
  };

  // 회원 PIN 번호 재설정 (점주가 새 6자리 PIN 직접 입력)
  const handleResetPin = async (phoneNum: string) => {
    if (!phoneNum) {
      return swalReturnWarning('회원 휴대폰번호를 확인할 수 없습니다.');
    }
    const { value: pinNum } = await Swal.fire({
      title: 'PIN 번호 재설정',
      input: 'text',
      inputLabel: '새 PIN 번호 (숫자 6자리)',
      text: '재설정하면 회원에게 새 PIN 번호가 문자로 발송됩니다.',
      inputAttributes: { maxlength: '6', inputmode: 'numeric' },
      showCancelButton: true,
      confirmButtonText: '저장',
      cancelButtonText: '취소',
      inputValidator: (value) =>
        /^\d{6}$/.test(value) ? null : 'PIN 번호는 숫자 6자리로 입력해주세요.',
    });
    if (!pinNum) return;
    try {
      const res = await createRequest(patchResetUserPin, phoneNum, pinNum);
      swalMessage('success', res?.data?.message || 'PIN 번호가 재설정되었습니다.');
    } catch (error) {
      catchError(error);
      swalMessage('error', 'PIN 재설정에 실패했습니다.\n다시 시도해주세요.');
    }
  };

  const parentPhoneNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    var regex = /[^0-9]/g;
    setParentPhoneNum(e.target.value.replace(regex, ''));
  };

  useEffect(() => {
    if (!merchantId) return;
    initDetailsModal(merchantId);
  }, [merchantId]);

  const onChangeEvent = () => {
    let newValue;
    if (+superEnterCheck === 1) {
      newValue = 0;
      setSuperEnterCheck(0);
    } else if (+superEnterCheck === 0) {
      newValue = 1;
      setSuperEnterCheck(1);
    }
    if (newValue !== undefined) {
      saveUserSettings({ superEnter: newValue });
    }
  };

  return (
    <>
      <style>{buildModalStyle(zIndex)}</style>
      <Modal
        style={{
          minWidth: window.innerWidth < 768 ? '80vw' : window.innerWidth < 480 ? '' : '60vw',
        }}
        centered
        isOpen={isOpen}
        toggle={accessChange}
        zIndex={zIndex}
        modalClassName="details-modal"
        backdropClassName="details-modal-backdrop">
        {isLoading && <BackgroundLoading />}
        {seat?.phoneNum ? (
          <ModalHeader
            style={{ display: ' flex', alignItems: 'center' }}
            toggle={() => {
              accessChange();
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ wordBreak: 'keep-all' }}>
                <span style={{ userSelect: 'text' }}>{seat?.phoneNum}</span> 회원정보
              </span>
            </div>
          </ModalHeader>
        ) : (
          <ModalHeader
            className="modal-title"
            toggle={() => {
              accessChange();
            }}>
            지점정보가 이전된 회원정보입니다.
          </ModalHeader>
        )}

        {seat?.phoneNum && (
          <>
            <ModalBody>
              <Disclaimer
                type="info"
                message="바코드 출력 기능은 '회원 정보' 탭 '회원별 설정 및 관리'에서 확인하실 수 있습니다."
                dense
              />
            </ModalBody>

            <TabList1>
              <div style={{ display: 'flex' }}>
                <TabButton selected={tabMenuBar === 5} onClick={() => setTabMenuBar(5)}>
                  회원 정보
                </TabButton>
                <TabButton selected={tabMenuBar === 0} onClick={() => setTabMenuBar(0)}>
                  이용권
                </TabButton>
                <TabButton selected={tabMenuBar === 1} onClick={() => setTabMenuBar(1)}>
                  입퇴장
                </TabButton>
                <TabButton selected={tabMenuBar === 2} onClick={() => setTabMenuBar(2)}>
                  결제정보
                </TabButton>
                <TabButton selected={tabMenuBar === 4} onClick={() => setTabMenuBar(4)}>
                  마일리지
                </TabButton>
                <TabButton selected={tabMenuBar === 3} onClick={() => setTabMenuBar(3)}>
                  메모
                </TabButton>
              </div>
            </TabList1>
            <ModalBody
              style={{
                minHeight: window.innerHeight > 480 ? '65vh' : '77.2vh',
              }}>
              {seat?.phoneNum && tabMenuBar === 0 && (
                <TicketTab
                  merchantId={seat?.merchantId || merchantId}
                  phoneNum={seat.phoneNum}
                  setIsLoading={setIsLoading}
                />
              )}

              {tabMenuBar === 1 && (
                <LogTab phoneNum={seat.phoneNum} setIsLoading={setIsLoading} />
              )}

              {tabMenuBar === 2 && (
                <PayTab
                  merchantId={seat?.merchantId || merchantId}
                  phoneNum={seat.phoneNum}
                  setIsLoading={setIsLoading}
                />
              )}
              {tabMenuBar === 3 && (
                <>
                  <UserInfoTableContainer>
                    {/* 메모 */}
                    <div className="section-title">메모</div>
                    <MemoCreateContainer>
                      <MemoTextareaWrapper>
                        <textarea
                          id="textarea"
                          maxLength={800}
                          placeholder="800자 이내로 내용을 작성하세요."
                          value={memo}
                          onChange={memoHandler}
                        />
                        <MemoTextLength>
                          <span>{memo.length}</span> / 800자
                        </MemoTextLength>
                      </MemoTextareaWrapper>
                      <Button buttonType="action" onClick={submitHandler}>
                        메모 작성
                      </Button>
                    </MemoCreateContainer>

                    <MemoListContainer>
                      <h4>작성 내역</h4>
                      {memolist.length === 0 ? (
                        <NoMemoMessage>등록된 메모가 없습니다.</NoMemoMessage>
                      ) : (
                        <MemoList>
                          {memolist.map((memoItem) => (
                            <MemoListItem key={memoItem.id}>
                              <MemoContent>{memoItem.memo}</MemoContent>
                              <MemoFooter>
                                <MemoTimestamp>
                                  {dayjs(memoItem.createdAt).format('YYYY.MM.DD HH:mm')}
                                </MemoTimestamp>
                                <MemoDeleteBtn onClick={() => deleteHandler(memoItem.id)}>
                                  삭제
                                </MemoDeleteBtn>
                              </MemoFooter>
                            </MemoListItem>
                          ))}
                        </MemoList>
                      )}
                    </MemoListContainer>
                  </UserInfoTableContainer>
                </>
              )}
              {tabMenuBar === 5 && (
                <>
                  <UserInfoTableContainer>
                    {/* 회원 기본 정보 */}
                    <div className="section-title">회원 기본 정보</div>
                    <StyledUserInfoTable>
                      <tbody>
                        <tr>
                          <td className="key">이름</td>
                          <td className="value">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #e1e1e1',
                                borderRadius: '4px',
                                width: '100%',
                              }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="key">전화번호</td>
                          <td className="value">{Format.formatPhone(seat?.phoneNum) || '-'}</td>
                        </tr>
                        <tr>
                          <td className="key">성별</td>
                          <td className="value">
                            <select
                              value={editGender === 0 ? '0' : editGender === 1 ? '1' : ''}
                              onChange={(e) => setEditGender(Number(e.target.value))}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #e1e1e1',
                                borderRadius: '4px',
                                width: '100%',
                                background: '#fff',
                              }}>
                              <option value="">선택</option>
                              <option value="0">남성</option>
                              <option value="1">여성</option>
                            </select>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">회원 유형</td>
                          <td className="value">
                            <select
                              value={String(editUserType)}
                              onChange={(e) => setEditUserType(Number(e.target.value))}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #e1e1e1',
                                borderRadius: '4px',
                                width: '100%',
                                background: '#fff',
                              }}>
                              <option value="0">학생</option>
                              <option value="1">성인</option>
                            </select>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">생년월일</td>
                          <td className="value">
                            <input
                              type="text"
                              value={editBirth}
                              placeholder="YYMMDD 또는 YYYY-MM-DD"
                              onChange={(e) => setEditBirth(e.target.value)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #e1e1e1',
                                borderRadius: '4px',
                                width: '100%',
                              }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="key">가입일자</td>
                          <td className="value">
                            {merchantUserInfo?.createdAt
                              ? dayjs(merchantUserInfo.createdAt).format('YYYY-MM-DD')
                              : '-'}
                          </td>
                        </tr>
                        <tr>
                          <td className="key">보호자 전화번호</td>
                          <td className="value">
                            <input
                              type="text"
                              name="parentPhoneNum"
                              value={Format.formatPhone(parentPhoneNum) || ''}
                              onChange={(e) => parentPhoneNumChange(e)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #e1e1e1',
                                borderRadius: '4px',
                                width: '100%',
                              }}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="key">보호자명</td>
                          <td className="value">
                            <input
                              type="text"
                              name="parentName"
                              value={editParentName}
                              onChange={(e) => setEditParentName(e.target.value)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #e1e1e1',
                                borderRadius: '4px',
                                width: '100%',
                              }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </StyledUserInfoTable>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: '1.5rem',
                      }}>
                      <Button buttonType="action" onClick={saveBasicUserInfo}>
                        회원 정보 저장
                      </Button>
                    </div>

                    {/* 회원별 설정 및 관리 */}
                    <div className="section-title">회원별 설정 및 관리</div>
                    <StyledUserInfoTable>
                      <tbody>
                        <tr>
                          <td className="key">슈퍼입장</td>
                          <td className="value">
                            <ToggleBtn>
                              <input
                                type="checkbox"
                                value={superEnterCheck}
                                checked={superEnterCheck === 1}
                                disabled={superEnterCheck === 2}
                                onChange={onChangeEvent}
                              />
                              <span className="onoff-switch"></span>
                            </ToggleBtn>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">문자 차단 여부</td>
                          <td className="value">
                            <ToggleBtn>
                              <input
                                type="checkbox"
                                checked={smsDenyCheck}
                                onChange={() => {
                                  const newValue = !smsDenyCheck;
                                  setSmsDenyCheck(newValue);
                                  saveUserSettings({ smsDeny: newValue });
                                }}
                              />
                              <span className="onoff-switch"></span>
                            </ToggleBtn>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">보호자 알림 수신</td>
                          <td className="value">
                            <ToggleBtn>
                              <input
                                type="checkbox"
                                checked={parentAlertConsentCheck === 1}
                                onChange={async () => {
                                  const newValue = parentAlertConsentCheck === 1 ? 0 : 1;
                                  setParentAlertConsentCheck(newValue);
                                  try {
                                    await createRequest(userInfoUpdate, {
                                      merchantId: seat?.merchantId,
                                      userId,
                                      parentAlertConsent: newValue,
                                    });
                                    if (setData) {
                                      setData(seat.merchantId);
                                    }
                                  } catch (error) {
                                    // 저장 실패 시 토글 원복
                                    setParentAlertConsentCheck(newValue === 1 ? 0 : 1);
                                    catchError(error);
                                  }
                                }}
                              />
                              <span className="onoff-switch"></span>
                            </ToggleBtn>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">QR/PIN 제한해제</td>
                          <td className="value">
                            <ToggleBtn>
                              <input
                                type="checkbox"
                                checked={accessCheck}
                                onChange={() => {
                                  const newValue = !accessCheck;
                                  setAccessCheck(newValue);
                                  saveUserSettings({ access: newValue });
                                }}
                              />
                              <span className="onoff-switch"></span>
                            </ToggleBtn>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">바코드 출력</td>
                          <td className="value">
                            <button
                              className="btn-primary btn-sm"
                              onClick={() =>
                                handleIssueBarcode(seat.merchantId, seat.phoneNum, seat.qrCode)
                              }>
                              바코드 출력
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">바코드 문자 발송</td>
                          <td className="value">
                            <button
                              className="btn-primary btn-sm"
                              onClick={() => handleSendBarcode(seat.phoneNum)}>
                              바코드 문자 발송
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">PIN 재설정</td>
                          <td className="value">
                            <button
                              className="btn-primary btn-sm"
                              onClick={() => handleResetPin(seat.phoneNum)}>
                              PIN 재설정
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td className="key">얼굴 사진</td>
                          <td className="value">
                            {faceInfo.hasFace && faceInfo.faceImg ? (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                }}>
                                <img
                                  src={faceInfo.faceImg}
                                  alt="회원 얼굴 사진"
                                  style={{
                                    width: '72px',
                                    height: '72px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                  }}
                                />
                                <button
                                  className="btn-primary btn-sm"
                                  onClick={() => handleDeleteFace(seat.phoneNum)}>
                                  얼굴 삭제
                                </button>
                              </div>
                            ) : (
                              <span>등록된 얼굴 사진이 없습니다.</span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </StyledUserInfoTable>
                  </UserInfoTableContainer>
                </>
              )}
              {tabMenuBar === 4 && (
                <MileageTab
                  merchantId={seat?.merchantId || merchantId}
                  userId={userId}
                  seat={seat}
                  setIsLoading={setIsLoading}
                />
              )}
            </ModalBody>
          </>
        )}
      </Modal>
      {back && (
        <ModalBackground
          onClick={() => {
            accessChange();
          }}
        />
      )}
    </>
  );
};

export default DetailsModal;
